import { SivaContractOverviewDto, SivaPhase } from '../../../../api/v2';
import React, { useMemo, useState } from 'react';
import { Tabs } from '@innovationnorway/cl-react';
import { ColumnDef } from '@tanstack/react-table';
import {
  admissionDateColumn,
  advisorColumn,
  companyNameOrIdeaNameColumn,
  endDateColumn,
  esaColumn,
  goalReportingColumn,
  numericIdColumn,
  phaseColumn,
  registrationDateColumn,
  selfDeclarationColumn,
  statusColumn,
  municipalityColumn,
  contactEmail,
  industryColumn,
} from './use-siva-overview-columns';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

export interface TabDefinition {
  name: 'targetCompanies' | 'ideas' | 'contracts' | 'alumni';
  availablePhases: SivaPhase[];
  columns: ColumnDef<SivaContractOverviewDto, any>[];
}

export const useSivaOverviewTabs = (isBusinessGarden: boolean) => {
  const { t: tSiva } = useTranslation('siva');

  const ideasTab = useMemo(
    () => ({
      name: 'ideas' as const,
      availablePhases: [SivaPhase.Idea],
      columns: [
        companyNameOrIdeaNameColumn,
        statusColumn,
        numericIdColumn,
        advisorColumn,
        registrationDateColumn,
        municipalityColumn,
        contactEmail,
        industryColumn,
      ],
    }),
    [],
  );

  const contractsTab = useMemo(
    () => ({
      name: 'contracts' as const,
      availablePhases: [SivaPhase.PreIncubation, SivaPhase.Incubated, SivaPhase.Fi, SivaPhase.ScaleUp],
      columns: [
        companyNameOrIdeaNameColumn,
        phaseColumn,
        admissionDateColumn,
        advisorColumn,
        selfDeclarationColumn,
        goalReportingColumn,
        esaColumn,
        numericIdColumn,
        municipalityColumn,
        contactEmail,
        industryColumn,
      ],
    }),
    [],
  );

  const targetedTab = useMemo(
    () => ({
      name: 'targetCompanies' as const,
      availablePhases: [SivaPhase.Targeted],
      columns: [
        companyNameOrIdeaNameColumn,
        admissionDateColumn,
        advisorColumn,
        selfDeclarationColumn,
        goalReportingColumn,
        esaColumn,
        numericIdColumn,
        municipalityColumn,
        contactEmail,
        industryColumn,
      ],
    }),
    [],
  );

  const alumniTab = useMemo(
    () => ({
      name: 'alumni' as const,
      availablePhases: [SivaPhase.Alumni],
      columns: [
        companyNameOrIdeaNameColumn,
        statusColumn,
        numericIdColumn,
        advisorColumn,
        endDateColumn,
        esaColumn,
        municipalityColumn,
        contactEmail,
      ],
    }),
    [],
  );

  const tabs: TabDefinition[] = useMemo(() => {
    return isBusinessGarden ? [targetedTab, alumniTab] : [contractsTab, ideasTab, alumniTab];
  }, [contractsTab, ideasTab, targetedTab, alumniTab, isBusinessGarden]);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialState = tabs.find((tab) => tab.name === searchParams.get('tab'));
  const [activeTabName, setActiveTabName] = useState(initialState?.name || tabs[0].name);

  const activeTab = useMemo(
    () => tabs.find((t) => t.name === activeTabName) ?? tabs[0],
    [activeTabName, tabs],
  );

  const handleChangeTab = (tabIndex: number) => {
    setActiveTabName(tabs[tabIndex].name);
    setSearchParams({ tab: tabs[tabIndex].name }, { replace: true });
  };

  const tabElements = (
    <Tabs value={activeTabName} className="margin-top--6">
      <Tabs.List className="margin-bottom--2">
        {tabs.map((tab, tabIndex) => (
          <Tabs.Tab
            key={tab.name}
            value={tab.name}
            onClick={() => handleChangeTab(tabIndex)}
            aria-label={tSiva(`overviewTabs.${tab.name}`)}
          >
            {tSiva(`overviewTabs.${tab.name}`)}
          </Tabs.Tab>
        ))}
      </Tabs.List>
    </Tabs>
  );

  return {
    tabElements,
    availablePhasesForTab: activeTab.availablePhases,
    columnsForTab: activeTab.columns,
    activeTabName: activeTabName,
  };
};
